import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  IconButton,
  InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CloudUpload, CheckCircle, Error, ArrowBack, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import BankTransaction from '../../models/bankTransaction';
import { jwtDecode } from "jwt-decode";

const BankTransactionImport = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Nhập dữ liệu giao dịch ngân hàng');
    }
  }, [setPageTitle]);

  // Check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserRole(decodedToken.role);
      if (decodedToken.role !== 'ADMIN') {
        navigate('/');
      }
    }
  }, [navigate]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError('Vui lòng chọn file để tải lên');
      return;
    }

    // Check file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
      setError('Chỉ hỗ trợ file Excel (.xlsx, .xls)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (password) {
        formData.append('password', password);
      }

      // For debugging purposes - Log file info
      console.log('File being uploaded:', {
        name: file.name,
        type: file.type,
        size: file.size,
        hasPassword: Boolean(password)
      });

      const bankTransaction = new BankTransaction();
      const response = await bankTransaction.importBankTransactions(formData);

      if (response.success) {
        // Always include the message from the API response
        setResult({
          message: response.message,
          imported: response.imported || 0,
          skipped: response.skipped || 0
        });
      } else {
        setError(response.message || 'Đã xảy ra lỗi khi nhập dữ liệu');
      }
    } catch (error) {
      console.error('Error importing bank transactions:', error);
      setError('Đã xảy ra lỗi khi nhập dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  if (userRole !== 'ADMIN') {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/bank-transactions')}
            sx={{ mr: 2 }}
          >
            Quay lại
          </Button>
          <Typography variant="h5" component="h1">
            Nhập dữ liệu giao dịch ngân hàng
          </Typography>
        </Box>

        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          {result ? (
            <Box sx={{ textAlign: 'center' }}>
              {result.imported > 0 ? (
                <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
              ) : result.skipped > 0 && result.imported === 0 ? (
                  <Error color="warning" sx={{ fontSize: 60, mb: 2 }} />
                ) : (
                    <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                  )}

              <Typography variant="h6" gutterBottom>
                {result.message || (result.imported > 0 ? 'Nhập dữ liệu thành công!' : 
                  result.skipped > 0 && result.imported === 0 ? 'Không có giao dịch mới để nhập!' :
                    'Nhập dữ liệu thành công!')}
              </Typography>

              <List>
                {result.imported > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Đã nhập ${result.imported} giao dịch`} 
                    />
                  </ListItem>
                )}
                {result.skipped > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <Error color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Đã bỏ qua ${result.skipped} giao dịch trùng lặp`} 
                    />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFile(null);
                    setPassword('');
                    setResult(null);
                  }}
                >
                  Nhập file khác
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/bank-transactions')}
                >
                  Xem danh sách giao dịch
                </Button>
              </Box>
            </Box>
          ) : (
              <form onSubmit={handleSubmit}>
                <Typography variant="body1" gutterBottom>
                  Tải lên file Excel (.xlsx, .xls) chứa dữ liệu giao dịch ngân hàng
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ my: 2 }}>
                    <AlertTitle>Lỗi</AlertTitle>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    my: 3,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(0, 0, 0, 0.01)'
                    }
                  }}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    {file ? file.name : 'Kéo thả file vào đây hoặc nhấp để chọn file'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Chỉ hỗ trợ file Excel (.xlsx, .xls)
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Mật khẩu file (nếu có)"
                  placeholder="Để trống nếu file không có mật khẩu"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  helperText="Chỉ điền nếu file Excel được bảo vệ bằng mật khẩu (ví dụ: sao kê BIDV)"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: password ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!file || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Đang xử lý...' : 'Tải lên và xử lý'}
                </Button>
              </form>
            )}
        </Paper>
      </Box>
    </Container>
  );
};

export default BankTransactionImport;
